import Script from 'next/script'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'

export function useJWPlayer({
  loadScript,
  videoIds,
}: {
  loadScript: boolean
  videoIds: string[]
}): useJWPlayerReturn {
  const renderedIds = useRef<string>('')
  const [jwVideoControllers, setJWVideoControllers] = useState<
    Record<string, jwplayer.JWPlayer>
  >({})

  const loadVideos = useCallback((videoIds: string[]): void => {
    videoIds.forEach((videoId) => {
      if (typeof window.jwplayer === 'undefined') return

      const videoController = window.jwplayer(videoId)

      videoController.setup({
        playlist: `https://cdn.jwplayer.com/v2/media/${videoId}`,
        aspectratio: '',
        height: '100%',
        width: '100%',
      })

      setJWVideoControllers((previousValue) => {
        return { ...previousValue, [videoId]: videoController }
      })
    })
  }, [])

  useEffect(() => {
    if (loadScript) {
      if (
        typeof window.jwplayer !== 'undefined' &&
        renderedIds.current !== JSON.stringify(videoIds)
      ) {
        renderedIds.current = JSON.stringify(videoIds)
        loadVideos(videoIds)
      } else {
        const portalElement = document.getElementById('dynamicScripts')

        if (portalElement) {
          const root = createRoot(portalElement)
          root.render(
            <Script
              src="https://cdn.jwplayer.com/libraries/2bfBS2E0.js" // The ID 2bfBS2E0 related to the specific player that is configured in JW Player Admin
              strategy="afterInteractive"
              onLoad={() => loadVideos(videoIds)}
            />
          )
        }
      }
    }
  }, [loadScript, loadVideos, videoIds])

  useEffect(() => {
    if (window.jwplayer && renderedIds.current !== JSON.stringify(videoIds)) {
      loadVideos(videoIds)
    }

    renderedIds.current = JSON.stringify(videoIds)
  }, [loadVideos, videoIds])

  return {
    jwVideoControllers,
  }
}

export interface useJWPlayerReturn {
  jwVideoControllers: Record<string, jwplayer.JWPlayer>
}
